'use client'

/**
 * The opposite-moving marquee rows — the canvas `.dealmarq-track` with two
 * identical `.dealmarq-set`s (the second aria-hidden) for the seamless loop,
 * and the `.dealcard` anatomy verbatim. A deal row carries the price-drop
 * badge, the orange percent pill, the was/save pair and the 60-day median
 * line; a plain row (top/bottom freezer) carries the lowest-price line.
 *
 * `DealCard` is the house deal card wherever a deal is shown, not only inside a
 * marquee: /deals (FRIDGE-871) renders it in a grid. Two optional props exist
 * for that, and neither changes what the home page renders:
 *   · `noImageFallback` — the ONE no-image slot filler (FRIDGE-239) for a card
 *     whose row carries no photo. Absent, the card keeps SafeImage's own
 *     brand-logo-then-house-mark chain.
 *   · `priority` — the LCP hint for the first cards of a grid that is above the
 *     fold. A marquee is never the LCP, so it never passes it.
 * `data-deal-price` / `data-deal-save` are stable crawler-text hooks: the
 * arithmetic guard in lib/crawler-text/guard.ts reads the current price and the
 * saving from them, so a style change can never quietly disable that check.
 */
import { DealMiniChart } from './deal-mini-chart'
import { useRef, type ReactNode } from 'react'
import { useIsHydrated } from '@/hooks/use-is-hydrated'
import { useMovingRail } from '@/hooks/use-moving-rail'
import { MARQUEE_LOOP_MIN } from '@/lib/homepage-2026/drop-rails'
import { MovingRailControls } from '@/components/brand-kit/moving-rail-controls'
import Link from 'next/link'
import { CardImage } from '@/components/brand-kit/card-image'
import { Badge } from '@/components/badges/badge'
import { badgesFromTypes } from '@/components/badges/select'
import type { HomeDeal, HomeProduct } from '@/lib/homepage-2026/types'
import { HeartGlyph } from '@/components/chrome/save-compare-controls'
import { dealLinkName, formatPrice, homeDealCtaLabel, productLinkName } from './format'
import { useSavedCompare } from './saved-compare'
import { toSavedItem } from './product-card'

export function DealCard({
  product,
  deal,
  noImageFallback,
  priority = false,
  duplicate = false,
  miniChart = true,
}: {
  product: HomeProduct
  deal?: HomeDeal
  /** The 30-day caption and sparkline; the homepage marquees drop them (owner 09/16/2026: "remove this"). */
  miniChart?: boolean
  /** Rendered in the media slot when the row carries no photo (see the header note). */
  noImageFallback?: ReactNode
  priority?: boolean
  duplicate?: boolean
}) {
  const sc = useSavedCompare()
  const saved = sc.isSaved(product.id)
  const compared = sc.isCompared(product.id)
  const historyQualifies = deal?.history30?.kind === 'chart'
  const pct = deal && historyQualifies ? Math.round(deal.dropFraction * 100) : 0
  const badge = deal && historyQualifies
    ? (product.badges.find((b) => b.type === 'price_drop') ?? badgesFromTypes(['price_drop'])[0])
    : !deal ? product.badges.find((b) => b.type === 'energy_star') : undefined
  return (
    // `data-deal-history` declares WHY a deal card prints no saving: the saving, the "was" price
    // and the -% badge render only when the 30-day history qualifies (kind "chart"). The live
    // crawler-text guard (lib/crawler-text/guard.ts checkDealsPage) accepts a no-claim card only
    // when it carries this declaration, so dropping the saving wording can never silently switch
    // the arithmetic check off (FRIDGE-871).
    <div data-product-image-card className="dealcard" data-fin={product.fin} data-deal-history={deal ? (deal.history30?.kind ?? 'unavailable') : undefined}>
      <div className="dc-media">
        {!product.image && noImageFallback ? (
          noImageFallback
        ) : (
          <CardImage src={product.image} alternate={product.alternateImage} href={product.href} tabIndex={duplicate ? -1 : undefined} alt={productLinkName(product)} width={280} height={144} priority={priority} />
        )}
        {/* A deal card carries the -N% pill and the Save line; the badge pill on top of them painted under the image box
            and showed as a bare arc (owner 09/16/2026: "what is this? remove this"), so deal cards carry no badge pill. */}
        {badge && !deal ? (
          <span className="dc-badge">
            <Badge badge={badge} size="sm" className="rounded-full" />
          </span>
        ) : null}
        {/* FRIDGE-1097: a real button. The duplicated marquee track is the
            aria-hidden clone, so its copy stays out of the tab order. */}
        <button
          type="button"
          className="phe"
          tabIndex={duplicate ? -1 : 0}
          style={{ top: '40px', color: saved ? '#dc2626' : undefined }}
          onClick={() => sc.toggleSaved(toSavedItem(product))}
          aria-pressed={saved}
          aria-label={saved ? 'Remove from saved' : 'Save this product'}
        >
          <HeartGlyph filled={saved} className="i" />
        </button>
        {deal && pct > 0 ? (
          <span className="dc-pct">
            -{pct}%
          </span>
        ) : null}
      </div>
      <div className="dc-body">
        <span className="pbrand">{product.brand}</span>
        <span className="dc-title">
          <Link tabIndex={duplicate ? -1 : undefined} className="ptlink" href={product.href} aria-label={productLinkName(product)}>
            {product.title}
          </Link>
        </span>
        <span className="pmodel">
          {product.fin ? <span className="idchip">{product.fin}</span> : null}
          {/* FRIDGE-1031: its own flex item so it can ellipsize; the full model stays DOM text and rides the tooltip. */}
          {product.model ? <span className="pmtext" title={`Model ${product.model}`}>{`${product.fin ? ' · ' : ''}Model ${product.model}`}</span> : null}
        </span>
        <div className="dc-foot">
          <div className="dc-prices">
            <span data-deal-price="current" className="dc-price">{formatPrice(product.priceCents)}</span>
            {deal && historyQualifies ? <span className="pwas" data-deal-price="typical">{formatPrice(deal.median60Cents)}</span> : null}
            {deal && historyQualifies ? <span className="psave" data-deal-save="">Save {formatPrice(deal.dropCents)}</span> : null}
          </div>
          <span className="dc-note">
            {deal ? <>Recent typical {formatPrice(deal.median60Cents)} &mdash; 60-day tracked median</> : 'Lowest price across tracked retailers today'}
          </span>
          {deal && miniChart ? <DealMiniChart history={deal.history30} /> : null}
          <Link tabIndex={duplicate ? -1 : undefined} className="pcta" href={product.href} aria-label={dealLinkName(product)}>
            {homeDealCtaLabel(product.priceCents)}
          </Link>
          <div
            className="pcompare"
            role="checkbox"
            aria-checked={compared}
            tabIndex={duplicate ? -1 : 0}
            onClick={() => sc.toggleCompare(toSavedItem(product))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                sc.toggleCompare(toSavedItem(product))
              }
            }}
          >
            <span className="box" style={compared ? { background: '#000000', borderColor: '#000000' } : undefined} />
            Compare
          </div>
        </div>
      </div>
    </div>
  )
}

/** The inner of a `.dealmarq`: the track with its two sets. */
export function MarqueeTrack({ products, deals, reverse = false, label = 'product deals' }: { products: HomeProduct[]; deals?: HomeDeal[]; reverse?: boolean; label?: string }) {
  const railRef = useRef<HTMLDivElement>(null)
  const interactionRef = useRef<HTMLDivElement>(null)
  const looped = useIsHydrated()
  // FRIDGE-1480: the loop copies exist for the seamless drift of a full rail. A thin rail
  // (fewer than MARQUEE_LOOP_MIN models) renders its one set and drifts to its end instead,
  // so the same model is never on screen twice (the owner's screenshot held one deal, shown
  // three times).
  const loop = products.length >= MARQUEE_LOOP_MIN
  const motion = useMovingRail(railRef, products.length * (loop ? 2 : 1) + Number(looped), reverse ? -24 : 24, interactionRef)
  if (products.length === 0) return null
  const byId = new Map((deals ?? []).map((d) => [d.product.id, d]))
  const set = (hidden: boolean) => (
    // Loop copies support pointer actions; duplicate controls stay out of
    // keyboard order and the accessibility tree.
    <div data-rail-first={!hidden || undefined} data-rail-repeat={hidden || undefined} className="dealmarq-set" aria-hidden={hidden || undefined}>
      {products.map((p) => (
        <DealCard key={(hidden ? 'dup-' : '') + p.id} product={p} deal={byId.get(p.id)} duplicate={hidden} miniChart={false} />
      ))}
    </div>
  )
  return (
    <div ref={interactionRef} style={{ display: 'contents' }}>
      <MovingRailControls label={label} {...motion} />
      <div ref={railRef} role="region" aria-label={label} data-motion-direction={reverse ? 'right' : 'left'} className="overflow-x-auto cursor-grab select-none">
        <div className="dealmarq-track" style={{ animation: 'none', transform: 'none' }}>
          {set(false)}
          {looped && loop && set(true)}
          {looped && loop && set(true)}
        </div>
      </div>
    </div>
  )
}
